import { EditorView, ViewPlugin } from "@codemirror/view";
import { EditorState, Facet, StateEffect, StateField, Transaction } from "@codemirror/state";
import { TabModel, TabModelPackage, TabModelState, TabParseContext, TabParseWorker } from "@tab-edit/model";
import { ASTParser } from "./parsing/parser";
import { default_blueprint, NodeBlueprint } from "./parsing/nodes/generator/blueprint";

export class ASTLanguage extends TabModel {
    static state: StateField<ASTLanguageState>;
    static setState = StateEffect.define<ASTLanguageState>();
    static define(spec: {
        blueprint?: NodeBlueprint,
        languageData?: {[name: string]: any}
    }) {
        let parser = new ASTParser(spec.blueprint||default_blueprint);
        // TODO: revisit this to make sure that this modification is correct
        let data = TabModelPackage.defineDataFacet(spec.languageData);
        return new ASTLanguage(data, parser, languagePackage);
    }
}

class ASTLanguageState extends TabModelState {
    apply(tr: Transaction) {
        if (!tr.docChanged) return this;
        let newCx = this.context.apply(tr);
        return new ASTLanguageState(newCx);
    }

    static init(state: EditorState) {
        let parseState = TabParseContext.init(ASTParseContext, state, astLanguageFacet);
        return new ASTLanguageState(parseState);
    }
}

ASTLanguage.state = StateField.define<ASTLanguageState>({
    create: ASTLanguageState.init,
    update(value, tr) {
        for (let e of tr.effects) if (e.is(ASTLanguage.setState)) return e.value;
        if (tr.startState.facet(astLanguageFacet) !== tr.state.facet(astLanguageFacet)) return ASTLanguageState.init(tr.state);
        return value.apply(tr);
    }
})

let currentContext: ASTParseContext | null = null;
class ASTParseContext extends TabParseContext {
    getCurrentContext(): ASTParseContext | null {
        return currentContext;
    }
    setCurrentContext(ctx: ASTParseContext | null): void {
        currentContext = ctx;
    }
}

const parseWorker = ViewPlugin.fromClass(class extends TabParseWorker {
    get modelState() { return ASTLanguage.state }
    createStateEffect(state: ASTLanguageState) { return ASTLanguage.setState.of(new ASTLanguageState(state.context))}
}, {
    eventHandlers: {focus() { this.scheduleWork() }}
});

const astLanguageFacet = Facet.define<TabModel, TabModel|null>({
    combine(astLanguageFacets) { return astLanguageFacets.length ? astLanguageFacets[0] : null },
    enables: [ASTLanguage.state, parseWorker]
});

const languagePackage = new TabModelPackage(astLanguageFacet, parseWorker, () => ASTLanguage.state);

export function abstractSyntaxTree(state: EditorState) {
    return languagePackage.tree(state);
}
export function ensureAST(state: EditorState, upto: number, timeout?: number) {
    return languagePackage.ensureTree(state, upto, timeout);
}
export function astAvailable(state: EditorState, upto?: number) {
    return languagePackage.treeAvailable(state, upto);
}
export function astParserRunning(view: EditorView) {
    return languagePackage.parserRunning(view);
}

export { TabAST } from "./structures/tree";
export { NodeBlueprint } from "./parsing/nodes/generator/blueprint";